import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePrefersReducedMotion } from '../organic/motion';
import { LAYERS } from './layers';
import useModal from './useModal';

/*
 * A sheet: the Dialog that slides in from an edge of the screen (2026-10-21;
 * docs/FIELD_AI_VISION_SPEC.md section 9). The Field camera's gallery comes
 * from the right, its messages from the left, its controls from the bottom.
 *
 * It is a Dialog in all but its place on the screen, through the same
 * ./useModal.js: a required `title` names it; Escape, the x and the dimmed
 * page close it (the page not while `dirty`, nothing while `busy`); focus
 * goes in -- to its title, so a screen reader says where the person is --
 * stays in and goes back. A Dialog opened from inside it is the innermost
 * and takes Escape first, and floats above it (LAYERS.sheet < LAYERS.dialog).
 *
 *   <Sheet side="right" title={heading} onClose={close}>...</Sheet>
 *
 * `side` is physical, never mirrored in Hebrew: a sheet comes from the edge
 * it was pulled from (section 8). It slides in -- appears, with reduced
 * motion -- and a drag on its grip toward its own edge puts it away, as the x
 * does. The slide is an animation that leaves no transform behind, so a
 * Dialog inside, position: fixed, still covers the whole screen.
 *
 * `modal` false makes it a sheet beside a page that stays in use: nothing
 * dims or covers the page, a tap there reaches the page and does not close
 * the sheet, and Tab may leave it; Escape, the x, the grip and Back still
 * close it. The camera's controls sheet, where the picture above it is what
 * is being judged and a tap on it focuses there. `offset` (a CSS length)
 * rests a bottom sheet that far above the screen's edge: above the camera's
 * bar, so the shutter stays in reach while the focus is set.
 *
 * Rendered in place, like Dialog.
 */
export const SIDES = Object.freeze(['left', 'right', 'bottom']);

// How far the grip must be dragged, of the sheet's size, to put it away.
const DISMISS_AT = 0.3;

export default function Sheet({ open = true, ...props }) {
  if (!open) return null;
  return <OpenSheet {...props} />;
}

function OpenSheet({
  title, onClose, side = 'right', busy = false, dirty = false, showClose = true, modal = true,
  titleAs: Title = 'h2', actions, size, offset, layer = LAYERS.sheet, initialFocusRef, describedBy,
  testId, children,
}) {
  const { t } = useTranslation('common');
  const reduced = usePrefersReducedMotion();
  const titleId = useId();
  const panelRef = useRef(null);
  const titleRef = useRef(null);
  const closeButtonRef = useRef(null);
  const pressedInside = useRef(false);
  const grip = useRef(null);
  const [pulled, setPulled] = useState(0);
  useModal(panelRef, { onClose, busy, initialFocusRef: initialFocusRef || titleRef, skipRef: closeButtonRef,
    trap: modal });

  const close = () => { if (!busy) onClose?.(); };
  const where = SIDES.includes(side) ? side : 'right';

  // The grip: px toward the sheet's own edge, never past where it rests.
  const outward = (e) => {
    const from = grip.current;
    if (where === 'left') return from.x - e.clientX;
    if (where === 'right') return e.clientX - from.x;
    return e.clientY - from.y;
  };
  const onGripDown = (e) => {
    const rect = panelRef.current.getBoundingClientRect();
    grip.current = { id: e.pointerId, x: e.clientX, y: e.clientY,
      size: where === 'bottom' ? rect.height : rect.width };
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* a synthetic pointer */ }
  };
  const onGripMove = (e) => {
    if (grip.current?.id === e.pointerId) setPulled(Math.max(0, outward(e)));
  };
  const onGripUp = (e) => {
    if (grip.current?.id !== e.pointerId) return;
    const far = Math.max(0, outward(e));
    const { size: full } = grip.current;
    grip.current = null;
    setPulled(0);
    if (full > 0 && far >= full * DISMISS_AT) close();
  };
  const onGripCancel = () => { grip.current = null; setPulled(0); };

  let shift;
  if (pulled) shift = where === 'bottom' ? `translateY(${pulled}px)` : `translateX(${where === 'left' ? -pulled : pulled}px)`;

  return (
    <div style={{ ...overlayStyle, ...(modal ? null : besideStyle), zIndex: layer }}
      data-testid={testId ? `${testId}-backdrop` : undefined}
      onMouseDown={(e) => { pressedInside.current = e.target !== e.currentTarget; }}
      onClick={(e) => {
        const inside = pressedInside.current;
        pressedInside.current = false;
        if (!modal || e.target !== e.currentTarget || inside) return;
        if (!dirty) close();
      }}>
      <style>{SHEET_CSS}</style>
      <div ref={panelRef} role="dialog" aria-modal={modal ? 'true' : 'false'} aria-labelledby={titleId}
        aria-describedby={describedBy} tabIndex={-1} data-testid={testId} data-side={where}
        style={{
          ...panelStyle, ...PLACES[where](size), pointerEvents: 'auto',
          ...(where === 'bottom' && offset ? { bottom: offset, paddingBottom: 16 } : null),
          animation: reduced ? 'none' : `pn-sheet-in-${where} 220ms ease-out`,
          transform: shift,
        }}>
        <div style={{ ...gripAreaStyle, ...GRIP_AREAS[where] }} aria-hidden="true"
          data-testid={testId ? `${testId}-grip` : undefined}
          onPointerDown={onGripDown} onPointerMove={onGripMove} onPointerUp={onGripUp} onPointerCancel={onGripCancel}>
          <span style={{ ...gripStyle, ...(where === 'bottom' ? gripAcross : gripAlong) }} />
        </div>
        <div style={headerStyle}>
          <Title ref={titleRef} id={titleId} tabIndex={-1} style={titleBaseStyle}>{title}</Title>
          {actions}
          {showClose && (
            <button ref={closeButtonRef} type="button" onClick={close} disabled={busy}
              aria-label={t('close')} title={t('close')} style={closeStyle}
              data-testid={testId ? `${testId}-close` : undefined}>
              <span aria-hidden="true">{'×'}</span>
            </button>
          )}
        </div>
        <div style={bodyStyle}>{children}</div>
      </div>
    </div>
  );
}

// From off the screen to where it rests; no fill mode, so nothing remains.
const SHEET_CSS = '@keyframes pn-sheet-in-right{from{transform:translateX(100%)}}'
  + '@keyframes pn-sheet-in-left{from{transform:translateX(-100%)}}'
  + '@keyframes pn-sheet-in-bottom{from{transform:translateY(100%)}}';

// Physical edges on purpose (left is left in Hebrew too). A side sheet keeps
// its content clear of the grip on the side facing the middle.
const PLACES = {
  left: (size) => ({ top: 0, bottom: 0, left: 0, width: size || 'min(420px, 92vw)',
    borderRadius: '0 16px 16px 0', paddingLeft: 'max(16px, env(safe-area-inset-left))', paddingRight: 28 }),
  right: (size) => ({ top: 0, bottom: 0, right: 0, width: size || 'min(420px, 92vw)',
    borderRadius: '16px 0 0 16px', paddingRight: 'max(16px, env(safe-area-inset-right))', paddingLeft: 28 }),
  bottom: (size) => ({ left: 0, right: 0, bottom: 0, maxHeight: size || '72dvh',
    borderRadius: '16px 16px 0 0', paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }),
};

// The grip lies along the side that faces the middle of the screen.
const GRIP_AREAS = {
  left: { position: 'absolute', top: 0, bottom: 0, right: 0, width: 22, cursor: 'ew-resize' },
  right: { position: 'absolute', top: 0, bottom: 0, left: 0, width: 22, cursor: 'ew-resize' },
  bottom: { position: 'relative', height: 20, marginTop: -8, cursor: 'ns-resize' },
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.35)',
};

// Beside the page rather than over it: nothing dims it, and a tap there is the page's.
const besideStyle = { background: 'transparent', pointerEvents: 'none' };

const panelStyle = {
  position: 'fixed',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  padding: 16,
  background: 'var(--c-surface)',
  color: 'var(--c-text-body-fg)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
  outline: 'none',
};

const gripAreaStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  touchAction: 'none',
  flexShrink: 0,
};

const gripStyle = { display: 'block', borderRadius: 999, background: 'var(--c-border)' };
const gripAcross = { width: 44, height: 5 };
const gripAlong = { width: 5, height: 44 };

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBlockEnd: 12,
  flexShrink: 0,
};

const titleBaseStyle = { margin: 0, fontSize: 18, lineHeight: 1.3, flex: 1, minWidth: 0, outline: 'none' };

const closeStyle = {
  background: 'none',
  border: 'none',
  fontSize: 24,
  lineHeight: 1,
  minWidth: 44,
  minHeight: 44,
  padding: 0,
  cursor: 'pointer',
  color: 'var(--c-text-muted-fg)',
};

const bodyStyle = { flex: 1, minHeight: 0, overflowY: 'auto' };
